import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseApi, lessonApi, assessmentApi } from '../api/client';
import { Course, ModuleType, Lesson, Assessment } from '../types';
import {
  BookOpen, Clock, ChevronDown, ChevronRight,
  Play, CheckCircle, Lock, ClipboardList
} from 'lucide-react';
import toast from 'react-hot-toast';

export function CourseDetailPage() {
  const { courseId }                      = useParams<{ courseId: string }>();
  
  const navigate                          = useNavigate();
  const [course, setCourse]               = useState<Course | null>(null);
  const [modules, setModules]             = useState<ModuleType[]>([]);
  const [lessons, setLessons]             = useState<Record<string, Lesson[]>>({});
  const [assessments, setAssessments]     = useState<Assessment[]>([]);
  const [expandedModule, setExpanded]     = useState<string | null>(null);
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    if (!courseId) return;
    async function load() {
      try {
        const [courseRes, modulesRes, assessRes] = await Promise.all([
          courseApi.getOne(courseId!),
          lessonApi.getModules(courseId!),
          assessmentApi.getByCourse(courseId!),
        ]);
        setCourse(courseRes.data);
        setModules(modulesRes.data);
        setAssessments(assessRes.data);
        // Auto-expand first module
        if (modulesRes.data.length > 0) {
          setExpanded(modulesRes.data[0].id);
        }
      } catch (error: any) {
  if (error.response?.status === 401 || error.response?.status === 403) {
    return;
  }
  toast.error('Failed to load course');
} finally {
        setLoading(false);
      }
    }
    load();
  }, [courseId]);

  const loadLessons = async (moduleId: string) => {
    if (lessons[moduleId]) return; // already loaded
    try {
      const res = await lessonApi.getLessons(moduleId);
      setLessons(prev => ({ ...prev, [moduleId]: res.data }));
    } catch {
      toast.error('Failed to load lessons');
    }
  };

  const toggleModule = (moduleId: string) => {
    if (expandedModule === moduleId) {
      setExpanded(null);
    } else {
      setExpanded(moduleId);
      loadLessons(moduleId);
    }
  };

  const statusIcon = (status?: string) => {
    if (status === 'COMPLETED') return <CheckCircle size={14} className="text-green-400" />;
    if (status === 'IN_PROGRESS') return <Play size={14} className="text-yellow-400" />;
    return <Lock size={14} className="text-gray-600" />;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8 text-gray-500">
        Loading course...
      </div>
    );
  }

  if (!course) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Course header */}
      <div className="bg-gradient-to-br from-green-400/10 to-blue-500/10
                      border border-green-400/20 rounded-2xl p-6 mb-6">
        <div className="text-xs text-green-400 font-bold mb-2">
          {course.subject} · Grade {course.grade} · {course.board}
        </div>
        <h1 className="text-2xl font-black text-white mb-2">
          {course.title}
        </h1>
        {course.description && (
          <p className="text-gray-400 text-sm">{course.description}</p>
        )}
        <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <BookOpen size={12} /> {modules.length} modules
          </span>
          <span className="flex items-center gap-1">
            <ClipboardList size={12} /> {assessments.length} quizzes
          </span>
        </div>
      </div>

      {/* Modules */}
      <div className="space-y-3 mb-6">
        <h2 className="text-lg font-bold text-white">Course Content</h2>

        {modules.length === 0 ? (
          <div className="text-gray-500 text-sm">
            No modules yet.
          </div>
        ) : (
          modules.map(mod => (
            <div key={mod.id}
                 className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              {/* Module header */}
              <button
                onClick={() => toggleModule(mod.id)}
                className="w-full flex items-center justify-between
                           p-4 hover:bg-gray-800 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-gray-800
                                  flex items-center justify-center
                                  text-xs font-bold text-gray-400">
                    {mod.position}
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">
                      {mod.title}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <Clock size={10} />
                      {mod.estimatedMinutes} min
                    </div>
                  </div>
                </div>
                {expandedModule === mod.id
                  ? <ChevronDown size={16} className="text-gray-400" />
                  : <ChevronRight size={16} className="text-gray-400" />
                }
              </button>

              {/* Lessons list */}
              {expandedModule === mod.id && (
                <div className="border-t border-gray-800">
                  {!lessons[mod.id] ? (
                    <div className="p-4 text-gray-500 text-sm">
                      Loading lessons...
                    </div>
                  ) : lessons[mod.id].length === 0 ? (
                    <div className="p-4 text-gray-500 text-sm">
                      No lessons yet.
                    </div>
                  ) : (
                    lessons[mod.id].map(lesson => (
                      <button
                        key={lesson.id}
                        onClick={() => navigate(
                          `/courses/${courseId}/lessons/${lesson.id}`
                        )}
                        className="w-full flex items-center justify-between
                                   px-4 py-3 hover:bg-gray-800 transition-colors
                                   text-left border-b border-gray-800/50 last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          {statusIcon(lesson.progressStatus)}
                          <span className="text-sm text-gray-300">
                            {lesson.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock size={10} />
                            {lesson.estimatedMins}m
                          </span>
                          <span className="text-green-400">
                            +{lesson.xpReward} XP
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Assessments */}
      {assessments.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-white mb-3">Quizzes</h2>
          <div className="space-y-3">
            {assessments.map(assessment => (
              <div key={assessment.id}
                   className="bg-gray-900 border border-gray-800
                              rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-white text-sm">
                    {assessment.title}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Pass: {assessment.passScore}% ·
                    Max {assessment.maxAttempts} attempts
                    {assessment.timeLimitMins &&
                      ` · ${assessment.timeLimitMins} min`
                    }
                  </div>
                </div>
                <button
                  onClick={() => navigate(
                    `/courses/${courseId}/quiz/${assessment.id}`
                  )}
                  className="bg-green-400 text-gray-950 font-bold
                             px-4 py-2 rounded-lg text-xs
                             hover:bg-green-300 transition-colors"
                >
                  Take Quiz
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}